/**
 * Shared occupancy calculation helper — Task #1531.
 *
 * Single source of truth for computing occupancy figures across the dashboard
 * KPI card, Occupancy Overview panel, property-level metrics, and Rooms &
 * Pricing view. When `excludeInactive` is true, rooms with status "inactive"
 * are excluded from the denominator and vacant-room count so that rooms taken
 * offline for renovation or other operational reasons don't artificially
 * suppress the occupancy percentage.
 *
 * Bathrooms and shadow rooms are never included in occupancy counts here;
 * callers are expected to pre-filter the room list with `isBillableRoom()`
 * semantics (or equivalent) before passing it in when that matters.
 * Moving-in rooms count as occupied: they are committed to an incoming resident,
 * not available vacancy. The inactive-room toggle only changes the denominator.
 *
 * Edge case: if all rooms are inactive and `excludeInactive` is true, the
 * denominator becomes 0 and occupancyPct is returned as 0 (not NaN / ∞).
 */

export interface OccupancyStats {
  totalConsideredRooms: number;
  occupiedRooms: number;
  vacantRooms: number;
  inactiveRooms: number;
  occupancyPct: number;
}

export function computeOccupancy(
  rooms: Array<{ status: string }>,
  excludeInactive: boolean,
): OccupancyStats {
  const inactiveRooms = rooms.filter((r) => r.status === "inactive").length;
  const consideredRooms = excludeInactive
    ? rooms.filter((r) => r.status !== "inactive")
    : rooms;

  const totalConsideredRooms = consideredRooms.length;
  const occupiedRooms = consideredRooms.filter(
    (r) => r.status === "occupied" || r.status === "moving_in",
  ).length;
  // `needs_flip` is intentionally folded into vacantRooms here — it is NOT a
  // separate status bucket like inactive. Rooms not occupied or moving in count
  // as vacant unless excluded by the inactive toggle. Rooms & Pricing groups
  // needs_flip with vacant in its status filter; keep that classification here.
  const vacantRooms = Math.max(0, totalConsideredRooms - occupiedRooms);
  const occupancyPct =
    totalConsideredRooms === 0
      ? 0
      : Math.round((occupiedRooms / totalConsideredRooms) * 100);

  return { totalConsideredRooms, occupiedRooms, vacantRooms, inactiveRooms, occupancyPct };
}
